import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import type { TFunction } from "@msys/tolgee";
import {
  CardContainer,
  ErrorMessage,
  ModalContent,
  ModalDialog,
  ModalTitle,
  useFormatting,
} from "@msys/ui";
import {
  AttachFile as AttachFileIcon,
  MoveToInbox as MoveToInboxIcon,
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { DocType } from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import { Avatar } from "../../../commons/Avatar.js";
import { useCategories } from "../../../features/categories/useCategories.js";
import { CrmCreateFormPrefill } from "../../../features/crm/CrmCreateForm.js";
import { useCustomFieldConfig } from "../../../features/custom-fields/useCustomFieldConfig.js";
import { useAcceptPendingDocShareMutation } from "../../../features/pending-doc-shares/AcceptPendingDocShare.generated.js";
import { DeclinePendingDocShareModal } from "../../../features/pending-doc-shares/DeclinePendingDocShareModal.js";
import {
  QuickAcceptProjectModal,
  type UnfinishedQuickAcceptance,
} from "../../../features/pending-doc-shares/QuickAcceptProjectModal.js";
import { CreateProjectModal } from "../../../features/projects/modals/CreateProjectModal.js";
import { PendingDocSharePreviewModal } from "./PendingDocSharePreviewModal.js";
import {
  PendingDocSharesBox_PendingDocShareClientActorQuery,
  PendingDocSharesBox_PendingDocShareFragment,
  usePendingDocSharesBox_PendingDocShareClientActorQuery,
  usePendingDocSharesBox_PendingDocSharesQuery,
} from "./PendingDocSharesBox.generated.js";

// Mounting is gated by `quote.document.update` from the outside (see Dashboard), so the
// query below only runs for permitted users; the box owns its own fetching and
// renders nothing when there are no pending shares.
export function PendingDocSharesBox() {
  const { t } = useTranslate(["Dashboard", "Global"]);
  const client = useApolloClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasOrganisationPermission } = useUserData();
  const canDecline = hasOrganisationPermission("quote_document_decide");
  // Warm the cache for the quick-accept modal, so Accept waits for its prefill only.
  useCategories();
  useCustomFieldConfig("Project");

  const pendingDocSharesQuery = usePendingDocSharesBox_PendingDocSharesQuery({
    client,
  });
  const pendingDocSharesResult = pendingDocSharesQuery.data?.pendingDocShares;
  const pendingDocShares =
    pendingDocSharesResult?.__typename === "PendingDocSharesResult"
      ? pendingDocSharesResult.pendingDocShares
      : [];

  const [previewingShare, setPreviewingShare] =
    React.useState<PendingDocSharesBox_PendingDocShareFragment | null>(null);
  const [acceptingShare, setAcceptingShare] =
    React.useState<PendingDocSharesBox_PendingDocShareFragment | null>(null);
  const closeAcceptFlow = () => setAcceptingShare(null);
  const [acceptDocShare] = useAcceptPendingDocShareMutation({ client });
  const [decliningShareId, setDecliningShareId] = React.useState<string | null>(
    null
  );
  // Projects created for a share whose acceptance then failed, by share id.
  // Outlives the modal, so accepting the share again resumes the project
  // instead of creating a second one.
  const unfinishedAcceptancesRef = React.useRef(
    new Map<string, UnfinishedQuickAcceptance>()
  );

  const handleCreateOnly = (projectId: string, projectTitle: string) => {
    // The project exists either way; a failed refresh only leaves the accepted
    // share listed until the next load.
    void pendingDocSharesQuery.refetch().catch(() => {});
    if (previewingShare && previewingShare.id === acceptingShare?.id) {
      setPreviewingShare(null);
    }
    closeAcceptFlow();
    enqueueSnackbar(
      t("Project '{title}' created successfully.", {
        ns: "Dashboard",
        title: projectTitle,
      }),
      {
        variant: "success",
        action: (
          <Button
            component={Link}
            to={`/projects/${projectId}/overview`}
            color="inherit"
            size="small"
          >
            {t("Go to project", { ns: "Dashboard" })}
          </Button>
        ),
      }
    );
  };

  const handleDeclined = (declinedShareId: string) => {
    // The share is gone either way; a failed refresh only leaves it listed
    // until the next load.
    void pendingDocSharesQuery.refetch().catch(() => {});
    // Close the preview in the same update, so the deleted share's preview is
    // never re-queried.
    if (previewingShare && previewingShare.id === declinedShareId) {
      setPreviewingShare(null);
    }
    setDecliningShareId(null);
    unfinishedAcceptancesRef.current.delete(declinedShareId);
    enqueueSnackbar(t("Declined", { ns: "Dashboard" }), {
      variant: "success",
    });
  };

  if (pendingDocShares.length === 0) {
    return null;
  }

  return (
    <CardContainer
      id="pending-doc-shares-box"
      isExpandable={false}
      itemCount={pendingDocShares.length}
      Icon={<MoveToInboxIcon color="secondary" />}
      title={t("Inbox", { ns: "Dashboard" })}
    >
      <Stack spacing={1} sx={{ p: 1 }}>
        {pendingDocShares.map(pendingDocShare => (
          <PendingDocShareCard
            key={pendingDocShare.id}
            pendingDocShare={pendingDocShare}
            onPreview={() => setPreviewingShare(pendingDocShare)}
            onAccept={() => setAcceptingShare(pendingDocShare)}
          />
        ))}
      </Stack>

      {previewingShare && (
        <PendingDocSharePreviewModal
          pendingDocShareId={previewingShare.id}
          handleClose={() => setPreviewingShare(null)}
          onAccept={() => setAcceptingShare(previewingShare)}
          onDecline={
            canDecline
              ? () => setDecliningShareId(previewingShare.id)
              : undefined
          }
        />
      )}

      {decliningShareId && (
        <DeclinePendingDocShareModal
          pendingDocShareId={decliningShareId}
          handleClose={() => setDecliningShareId(null)}
          handleDeclined={() => handleDeclined(decliningShareId)}
        />
      )}

      {/* The quick flow's prefill resolves quotes only; every other document
          type keeps the full project-creation flow. */}
      {acceptingShare && acceptingShare.docType !== "QUOTE" && (
        <AcceptPendingDocShareModal
          pendingDocShare={acceptingShare}
          handleClose={closeAcceptFlow}
          handleAccepted={async (projectId, clientCrmOrganisationId) => {
            await acceptDocShare({
              variables: {
                input: {
                  id: acceptingShare.id,
                  acceptInProjectId: projectId,
                  clientCrmOrganisationId,
                },
              },
            });
            navigate(`/projects/${projectId}/overview`);
          }}
        />
      )}

      {acceptingShare && acceptingShare.docType === "QUOTE" && (
        <QuickAcceptProjectModal
          pendingDocShareId={acceptingShare.id}
          handleClose={closeAcceptFlow}
          handleCreateAndOpen={projectId => {
            // Navigate to the freshly created project. This also unmounts the
            // dashboard (and the preview modal, when accepting from it), so the
            // now-deleted pending share is never re-queried — avoiding a
            // "pending doc share not found" error from a stale refetch.
            navigate(`/projects/${projectId}/overview`);
          }}
          handleCreateOnly={handleCreateOnly}
          unfinishedAcceptance={
            unfinishedAcceptancesRef.current.get(acceptingShare.id) ?? null
          }
          onUnfinishedAcceptanceChange={unfinished => {
            if (unfinished) {
              unfinishedAcceptancesRef.current.set(
                acceptingShare.id,
                unfinished
              );
            } else {
              unfinishedAcceptancesRef.current.delete(acceptingShare.id);
            }
          }}
        />
      )}
    </CardContainer>
  );
}

function PendingDocShareCard({
  pendingDocShare,
  onPreview,
  onAccept,
}: {
  pendingDocShare: PendingDocSharesBox_PendingDocShareFragment;
  onPreview: () => void;
  onAccept: () => void;
}) {
  const { t } = useTranslate(["Dashboard", "Global"]);
  const { getFormattedPrice } = useFormatting();

  const docTypeLabel = getDocTypeLabel(pendingDocShare.docType, t);
  const location = pendingDocShare.projectLocation;
  const chipLabel = location
    ? `${docTypeLabel} • ${[location.postalCode, location.city]
        .filter(Boolean)
        .join(" ")}`
    : docTypeLabel;

  const content = (
    <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Avatar
            size="s"
            initials={pendingDocShare.sharingSystemOrganisationTitle.substring(
              0,
              2
            )}
          />
          <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
            {pendingDocShare.sharingSystemOrganisationTitle}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {moment(pendingDocShare.createdAt).fromNow()}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "baseline", justifyContent: "space-between" }}
        >
          <Typography variant="h4">{pendingDocShare.docTitle}</Typography>
          {pendingDocShare.netTotal != null && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ alignItems: "baseline" }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {t("net", { ns: "Dashboard" })}
              </Typography>
              <Typography variant="h4" noWrap>
                {getFormattedPrice(pendingDocShare.netTotal)}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack direction="row">
          <Chip
            size="small"
            variant="outlined"
            color="success"
            icon={<AttachFileIcon />}
            label={chipLabel}
          />
        </Stack>
      </Stack>
    </CardContent>
  );

  return (
    <Card variant="outlined">
      {/* Accept stays outside the action area, so it never opens the preview. */}
      {/* The preview (`pendingDocSharePreview`) only resolves quotes. */}
      {pendingDocShare.docType === "QUOTE" ? (
        <CardActionArea onClick={onPreview}>{content}</CardActionArea>
      ) : (
        content
      )}
      <Divider sx={{ mx: 1 }} />
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onAccept}
        >
          {t("Accept", { ns: "Global" })}
        </Button>
      </CardActions>
    </Card>
  );
}

function getDocTypeLabel(docType: DocType, t: TFunction<"Dashboard">) {
  switch (docType) {
    case "QUOTE":
      return t("Quote", { ns: "Dashboard" });
    case "REQUIREMENT":
      return t("Requirement", { ns: "Dashboard" });
    case "BUILDING":
      return t("Building", { ns: "Dashboard" });
    case "TEMPLATE":
      return t("Template", { ns: "Dashboard" });
    default:
      return assertNever(docType);
  }
}

type PendingDocShareClientActor = NonNullable<
  Extract<
    PendingDocSharesBox_PendingDocShareClientActorQuery["pendingDocShare"],
    { __typename: "PendingDocShareResult" }
  >["pendingDocShare"]["clientActor"]
>;

// The full project-creation flow for non-quote shares. The share's CLIENT
// actor is read here, for this one share, rather than on the inbox list: the
// field runs the CRM matcher, which the list must not pay for every row.
function AcceptPendingDocShareModal({
  pendingDocShare,
  handleClose,
  handleAccepted,
}: {
  pendingDocShare: PendingDocSharesBox_PendingDocShareFragment;
  handleClose: () => void;
  handleAccepted: (
    projectId: string,
    clientCrmOrganisationId: string | null
  ) => Promise<void>;
}) {
  const { t } = useTranslate(["Dashboard"]);
  const client = useApolloClient();

  const query = usePendingDocSharesBox_PendingDocShareClientActorQuery({
    client,
    variables: { id: pendingDocShare.id },
  });
  const result = query.data?.pendingDocShare;
  const loaded =
    result?.__typename === "PendingDocShareResult"
      ? result.pendingDocShare
      : null;

  if (query.loading || !loaded) {
    return (
      <ModalDialog maxWidth="md" handleClose={handleClose}>
        <ModalTitle
          title={t("Accept shared document", { ns: "Dashboard" })}
          handleClose={handleClose}
        />
        <ModalContent isLoading={query.loading}>
          {!query.loading && (
            <ErrorMessage
              message={t("The shared document could not be loaded", {
                ns: "Dashboard",
              })}
            />
          )}
        </ModalContent>
      </ModalDialog>
    );
  }

  return (
    <AcceptPendingDocShareCreateProjectModal
      clientActor={loaded.clientActor ?? null}
      handleClose={handleClose}
      handleAccepted={handleAccepted}
    />
  );
}

function AcceptPendingDocShareCreateProjectModal({
  clientActor,
  handleClose,
  handleAccepted,
}: {
  clientActor: PendingDocShareClientActor | null;
  handleClose: () => void;
  handleAccepted: (
    projectId: string,
    clientCrmOrganisationId: string | null
  ) => Promise<void>;
}) {
  const { t } = useTranslate(["Dashboard"]);
  const { enqueueSnackbar } = useSnackbar();

  // The matcher runs on the actor's raw fields, so a match can exist while
  // every visible field is withheld; the matched contact is then shown by its
  // CRM title rather than dropped.
  const crmOrganisation =
    clientActor?.matchedCrmCompanyId != null
      ? {
          id: clientActor.matchedCrmCompanyId,
          title:
            clientActor.matchedCrmCompanyTitle ||
            clientActor.companyName ||
            [clientActor.firstname, clientActor.familyname]
              .filter(Boolean)
              .join(" ") ||
            clientActor.email ||
            "",
        }
      : undefined;

  // The contact type comes from the actor's stored `companyOrIndividual`, never
  // from which name fields happen to be visible, and an INDIVIDUAL keeps the
  // sharer's stored salutation so the form does not invent one.
  const initialClientPrefill: CrmCreateFormPrefill | undefined =
    clientActor && clientActor.matchedCrmCompanyId == null
      ? clientActor.companyOrIndividual === "COMPANY"
        ? {
            contactType: "COMPANY",
            companyName: clientActor.companyName ?? undefined,
            email: clientActor.email ?? undefined,
          }
        : {
            contactType: "INDIVIDUAL",
            firstname: clientActor.firstname ?? undefined,
            familyname: clientActor.familyname ?? undefined,
            salutation: clientActor.salutation ?? undefined,
            email: clientActor.email ?? undefined,
          }
      : undefined;

  return (
    <CreateProjectModal
      title={t("Accept shared document", { ns: "Dashboard" })}
      pageName="Dashboard"
      desiredProjectInitialStatus="opportunity"
      crmOrganisation={crmOrganisation}
      initialClientPrefill={initialClientPrefill}
      handleClose={handleClose}
      handleComplete={async (projectId, crmOrganisationId) => {
        // Only forward a CRM mapping when the share actually has a CLIENT
        // actor to map; otherwise the backend would run a no-op CRM
        // lookup/validation.
        await handleAccepted(projectId, clientActor ? crmOrganisationId : null);
        enqueueSnackbar(t("Shared document accepted", { ns: "Dashboard" }));
      }}
    />
  );
}
